import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {SaleChannel} from '../../../shared/SaleChannel';
import {SaleChannelService} from '../../../services/saleChannel.service';
import {RouteHistoryService} from '../../../services/routeHistory.service';

interface Crumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.sass']
})
export class BreadcrumbsComponent implements OnInit {
  breadcrumbs: Crumb[] = [];
  saleChannels: SaleChannel[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private saleChanneService: SaleChannelService,
    private routeHistoryService: RouteHistoryService,
    private translate: TranslateService,
  ) {
    // Ścieżka NIE może zależeć od powodzenia zapytania sieciowego. Wcześniej
    // i pierwsze wyliczenie, i subskrypcja routera siedziały w tap() zapytania
    // o kanały sprzedaży — gdy padało (restart backendu, brak sieci),
    // breadcrumby nie powstawały w ogóle i pasek zostawał pusty.
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => this.runBreadcrumbs());
    this.translate.onLangChange.subscribe(() => this.runBreadcrumbs());

    // Kanały sprzedaży tylko WZBOGACAJĄ ścieżkę o nazwę sklepu w detalu —
    // ich brak nie może wywrócić reszty.
    this.getSaleChannels().subscribe({
      next: () => this.runBreadcrumbs(),
      error: () => { /* bez nazw sklepów; pozostałe poziomy ścieżki działają */ },
    });
  }

  ngOnInit(): void {
    // NIE w konstruktorze: tam drzewo aktywowanych tras jeszcze nie istnieje
    // i child.snapshot bywa undefined (wywracalo cala aplikacje).
    this.runBreadcrumbs();

    // Etykiety idą przez translate.instant(), więc gdy słownik nie zdążył się
    // wczytać, w ścieżce lądowały SUROWE KLUCZE („MENU.DOCUMENTS.EXPORTS”).
    // translate.get() czeka na loader i przeliczamy ścieżkę ponownie.
    this.translate.get('MENU.MAIN.HOME').subscribe(() => this.runBreadcrumbs());
  }

  runBreadcrumbs() {
    this.breadcrumbs = this.getBreadcrumbs(this.activatedRoute.root);
  }

  /**
   * Walks the *entire* activated-route tree (not just one level), so that
   * crumbs from nested/lazy-loaded feature modules (e.g. produkty-test,
   * hurtowi-test, urlopy) are included with a correctly accumulated URL.
   */
  getBreadcrumbs(route: ActivatedRoute): Crumb[] {
    const breadcrumbs: Crumb[] = [];
    let currentRoute: ActivatedRoute | undefined = route;
    let url = '';
    let isFirstSegment = true;
    let reconstructedFromHistory = false;

    while (currentRoute) {
      const child = currentRoute.children.find(c => c.outlet === 'primary');
      // snapshot bywa jeszcze niegotowy przy bardzo wczesnym wywolaniu
      if (!child || !child.snapshot) {
        break;
      }
      currentRoute = child;

      const segments: string[] = child.snapshot.url.map(segment => segment.path);
      if (segments.length) {
        // Some legacy routes combine static + param segments in one route
        // config (e.g. 'orders/list/:tab/:shop'). The crumb for that level
        // should only link to its *static* prefix (e.g. '/orders/list'),
        // not the full path including whichever param values are present.
        const configSegments = (child.routeConfig?.path ?? '').split('/').filter(Boolean);
        const paramIndex = configSegments.findIndex(s => s.startsWith(':'));
        const staticCount = paramIndex === -1 ? segments.length : paramIndex;
        const staticUrl = url + '/' + segments.slice(0, staticCount).join('/');

        url += '/' + segments.join('/');

        if (isFirstSegment) {
          breadcrumbs.push(...this.getPreviousCrumbs(segments[0]));
          isFirstSegment = false;
        }

        const labelKey = child.snapshot.data?.['breadcrumb'];
        const backTo: string | undefined = child.snapshot.data?.['breadcrumbBackTo'];

        if (labelKey) {
          breadcrumbs.push({label: this.translate.instant(labelKey), url: backTo ? '/' + backTo : staticUrl});
        }

        // Routes like .../add or .../edit/:id don't carry their parent
        // list's tab/shop context in their own URL. Recover it from the
        // last visited matching list URL so e.g. "Detal > Allegro" still
        // shows up (and is clickable) while editing an order.
        // Only the order/invoice list routes actually have :tab/:shop
        // segments — for every other backTo (profile, firmy, deposits...),
        // getLastMatchingRoute() would just match the current edit URL
        // itself (since it also contains the backTo fragment) and
        // misread the record's own :id as a bogus "shop" id.
        const backToHasTabShopContext = backTo === 'orders/list' || backTo === 'invoices/list';

        if (backTo && backToHasTabShopContext) {
          const historyEntry = this.routeHistoryService.getLastMatchingRoute(backTo);
          if (historyEntry) {
            const historyPath = historyEntry.url.split('?')[0];
            const baseSegCount = backTo.split('/').filter(Boolean).length;
            const extraSegments = historyPath.split('/').filter(Boolean).slice(baseSegCount);

            if (extraSegments[0]) {
              const tab = this.getTab(extraSegments[0]);
              if (tab) {
                breadcrumbs.push(tab);
                reconstructedFromHistory = true;
              }
            }
            if (extraSegments[1]) {
              const shop = this.getShop(extraSegments[1]);
              if (shop) {
                breadcrumbs.push(shop);
                reconstructedFromHistory = true;
              }
            }
          }
        }
      }

      const params = child.snapshot.params;
      if (params?.tab) {
        const tab = this.getTab(params.tab);
        if (tab) {
          breadcrumbs.push(tab);
        }
      }
      if (params?.shop) {
        const shop = this.getShop(params.shop);
        if (shop) {
          breadcrumbs.push(shop);
        }
      }
    }

    // The current page itself is never a link. When the trail was rebuilt
    // from navigation history (e.g. editing an order), none of the crumbs
    // are the actual current page, so all of them should stay clickable.
    if (breadcrumbs.length && !reconstructedFromHistory) {
      breadcrumbs[breadcrumbs.length - 1] = {...breadcrumbs[breadcrumbs.length - 1], url: ''};
    }

    return breadcrumbs;
  }

  getPreviousCrumbs( segment ): Crumb[] {
    const key = this.getPreviousCrumbKey(segment);
    return key ? [{label: this.translate.instant(key), url: ''}] : [];
  }

  private getPreviousCrumbKey( segment ): string | null {
    switch (segment) {
      case 'orders':
      case 'invoices':
      case 'eksporty':
        return 'MENU.MAIN.DOCUMENTS';
      case 'produkty':
      case 'produkty-test':
      case 'shipping':
        return 'MENU.MAIN.PRODUCT_BASE';
      case 'wholesaler':
      case 'hurtowi-test':
      case 'deposits':
        return 'MENU.MAIN.CLIENT_BASE';
      case 'profile':
      case 'privilages':
      case 'ustawienia':
        return 'MENU.MAIN.ADMIN';
      default:
        return null;
    }
  }

  private getTab(tab: string) {
    const key = this.getTabKey(tab);
    if (!key) {
      return null;
    }
    return {label: this.translate.instant(key.label), url: key.url};
  }

  private getTabKey(tab: string): {label: string; url: string} | null {
    switch (tab) {
      case '1':
        return {label: 'MENU.ORDERS.WHOLESALE_PL', url: '/orders/list/1'};
      case '2':
        return {label: 'MENU.ORDERS.WHOLESALE_FOREIGN', url: '/orders/list/2'};
      case '3':
        return {label: 'MENU.ORDERS.RETAIL', url: '/orders/list/3'};
      case 'pro':
        return {label: 'MENU.INVOICES.PROFORMAS', url: '/invoices/list/pro'};
      case 'vat':
        return {label: 'MENU.INVOICES.VAT', url: '/invoices/list/vat'};
      case 'kor':
        return {label: 'MENU.INVOICES.CORRECTIONS', url: '/invoices/list/kor'};
      default:
        return null;
    }
  }

  private getShop(shop: string) {
    const channel = this.saleChannels.filter(x => x.id==Number(shop))[0];
    if(channel){
      return {label: channel.name, url: '/orders/list/3/'+channel.id};
    }else{
      return null;
    }
  }

  getSaleChannels() {
    return this.saleChanneService.getSaleChannels().pipe(
      tap((res) => {
        this.saleChannels = res.filter(x => x.type=='detail');
      })
    );
  }
}
